/**
 * Crisis Monitor Component
 *
 * Monitors students in crisis and provides intervention tools for administrators.
 * Shows real-time crisis detection and allows manual intervention.
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  User,
  Clock,
  TrendingUp,
  HelpCircle,
  PlayCircle,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import HybridIntelligenceRouter from '../../services/intelligence/HybridIntelligenceRouter';
import { RecoveryPlanExecutor } from '../../services/intelligence/RecoveryPlanExecutor';
import { supabase } from '../../lib/supabase';

const CrisisMonitor = () => {
  const [usersInCrisis, setUsersInCrisis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [interventionPlan, setInterventionPlan] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    loadCrisisData();
    // Refresh every 5 minutes
    const interval = setInterval(loadCrisisData, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadCrisisData = async () => {
    setLoading(true);
    console.log('[CrisisMonitor] Starting crisis data load...');

    try {
      // Get all students from student_academic_profiles
      const { data: students, error: studentError } = await supabase
        .from('student_academic_profiles')
        .select('user_id, grade_level');

      if (studentError) throw studentError;

      // Get family member emails separately if needed
      const studentsWithEmails = await Promise.all(
        students.map(async (student) => {
          // Since user_id in student_academic_profiles = id in family_members
          const { data: familyMember } = await supabase
            .from('family_members')
            .select('email, name')
            .eq('id', student.user_id)
            .single();

          return {
            ...student,
            email: familyMember?.email || 'No email',
            display_name: familyMember?.name || 'Student'
          };
        })
      );

      console.log(`[CrisisMonitor] Found ${studentsWithEmails?.length || 0} students:`, studentsWithEmails);

      // For each student, get their tasks from timeline_items
      const usersWithTasks = await Promise.all(
        studentsWithEmails.map(async (student) => {
          let tasks = [];
          console.log(`[CrisisMonitor] Checking tasks for student: ${student.display_name} (ID: ${student.user_id})`);

          // Get tasks from timeline_items for this student
          // Ensure user_id is properly formatted as a string
          const userId = String(student.user_id);
          console.log(`[CrisisMonitor] Querying timeline_items for user_id: "${userId}" (type: ${typeof userId})`);

          const { data: timelineItems, error: timelineError } = await supabase
            .from('timeline_items')
            .select('*')
            .eq('user_id', userId)
            .or('completed.eq.false,completed.is.null');

          if (timelineItems && !timelineError && timelineItems.length > 0) {
            tasks = timelineItems;
            console.log(`[CrisisMonitor] ✓ Found ${tasks.length} incomplete tasks for ${student.display_name}`);
            if (tasks.length > 0) {
              console.log(`[CrisisMonitor] Sample task structure:`, tasks[0]);
            }
          } else if (timelineError) {
            console.log(`[CrisisMonitor] Error querying timeline_items:`, timelineError.message);
          } else {
            console.log(`[CrisisMonitor] No tasks found for ${student.display_name}`);
          }


          return {
            id: student.user_id,
            email: student.email,
            display_name: student.display_name,
            grade_level: student.grade_level,
            tasks
          };
        })
      );

      // Assess each user's crisis level
      console.log('[CrisisMonitor] Starting assessment phase...');

      const assessments = await Promise.all(
        usersWithTasks.map(async (user) => {
          const activeTasks = user.tasks || [];
          console.log(`[CrisisMonitor] Assessing ${user.email}: ${activeTasks.length} active tasks`);

          const now = new Date();
          const overdueTasks = activeTasks.filter(t => {
            // Try multiple date field names (timeline_items uses scheduled_date)
            const dateField = t.scheduled_date || t.deadline || t.due_date || t.target_date;

            if (!dateField) {
              console.log(`[CrisisMonitor] Task has no date field:`, t);
              return false;
            }

            const dueDate = new Date(dateField);
            const isOverdue = dueDate < now && !t.completed;

            if (isOverdue) {
              const taskTitle = t.title || t.name || t.description || t.task_title || 'Untitled task';
              console.log(`[CrisisMonitor] Overdue task found: "${taskTitle}" - Due: ${dateField}`);
            }

            return isOverdue;
          });

          // Quick assessment - check multiple priority field names
          const criticalOverdue = overdueTasks.filter(t => {
            const priority = t.priority || t.task_priority || t.importance;
            return priority === 'critical' || priority === 'high' || priority === 'urgent';
          });

          const riskAssessment = {
            overdueCount: overdueTasks.length,
            criticalOverdue: criticalOverdue.length,
            totalActiveTasks: activeTasks.length,
            averageReschedules: activeTasks.reduce((sum, t) => sum + (t.reschedule_count || t.rescheduled_count || 0), 0) / Math.max(1, activeTasks.length)
          };

          // Determine crisis level
          let level = 'normal';
          if (riskAssessment.overdueCount >= 10 || riskAssessment.criticalOverdue >= 3) {
            level = 'crisis';
          } else if (riskAssessment.overdueCount >= 5 || riskAssessment.criticalOverdue >= 2) {
            level = 'high_stress';
          } else if (riskAssessment.overdueCount >= 3) {
            level = 'moderate';
          }

          console.log(`[CrisisMonitor] User ${user.email} assessment:
            - Overdue: ${riskAssessment.overdueCount}
            - Critical Overdue: ${riskAssessment.criticalOverdue}
            - Total Active: ${riskAssessment.totalActiveTasks}
            - Crisis Level: ${level}
          `);

          return {
            ...user,
            riskAssessment,
            level,
            activeTasks,
            overdueTasks
          };
        })
      );

      // Filter to only show users in crisis or high stress
      const crisisUsers = assessments.filter(u =>
        u.level === 'crisis' || u.level === 'critical' || u.level === 'high_stress'
      );

      // Log assessment summary
      console.log(`[CrisisMonitor] Assessment Summary:
        Total Users: ${assessments.length}
        Crisis Users: ${crisisUsers.filter(u => u.level === 'crisis').length}
        High Stress Users: ${crisisUsers.filter(u => u.level === 'high_stress').length}
        Total Overdue Tasks: ${assessments.reduce((sum, u) => sum + u.riskAssessment.overdueCount, 0)}
      `);

      // Sort by severity
      crisisUsers.sort((a, b) => {
        const levelOrder = { critical: 0, crisis: 1, high_stress: 2 };
        return levelOrder[a.level] - levelOrder[b.level];
      });

      setUsersInCrisis(crisisUsers);
      setLoading(false);
    } catch (error) {
      console.error('[CrisisMonitor] Failed to load data:', error);
      setLoading(false);
    }
  };

  const generateInterventionPlan = async (user) => {
    setSelectedUser(user);
    try {
      const response = await HybridIntelligenceRouter.generateSuggestions(
        user.activeTasks,
        {
          userId: user.id,
          adminOverride: true,
          userRequestedHelp: true
        }
      );

      setInterventionPlan(response);
    } catch (error) {
      console.error('[CrisisMonitor] Failed to generate plan:', error);
      alert('Failed to generate intervention plan');
    }
  };

  const executePlan = async () => {
    if (!interventionPlan || !selectedUser) return;

    const executor = new RecoveryPlanExecutor();
    try {
      const result = await executor.executePlan(
        interventionPlan.actions,
        selectedUser.activeTasks,
        {
          userId: selectedUser.id,
          executionSource: 'admin_intervention',
          adminOverride: true
        }
      );

      alert(`Plan executed successfully! ${result.summary.tasksUpdated} tasks updated.`);

      // Refresh data
      await loadCrisisData();
      setInterventionPlan(null);
      setSelectedUser(null);
    } catch (error) {
      console.error('[CrisisMonitor] Failed to execute plan:', error);
      alert('Failed to execute intervention plan');
    }
  };

  const getSeverityColor = (level) => {
    const colors = {
      critical: 'bg-red-100 border-red-300 text-red-900',
      crisis: 'bg-orange-100 border-orange-300 text-orange-900',
      high_stress: 'bg-yellow-100 border-yellow-300 text-yellow-900',
      moderate: 'bg-blue-100 border-blue-300 text-blue-900'
    };
    return colors[level] || 'bg-gray-100 border-gray-300';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-orange-600" />
            Crisis Monitor
          </h2>
          <p className="text-gray-600 mt-1">
            Monitor and intervene with students experiencing overwhelm
          </p>
        </div>

        <button
          onClick={loadCrisisData}
          className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Crisis Summary */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">
              {usersInCrisis.filter(u => u.level === 'crisis' || u.level === 'critical').length}
            </div>
            <div className="text-sm text-gray-600">In Crisis</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">
              {usersInCrisis.filter(u => u.level === 'high_stress').length}
            </div>
            <div className="text-sm text-gray-600">High Stress</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {usersInCrisis.reduce((sum, u) => sum + u.riskAssessment.overdueCount, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Overdue Tasks</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-violet-600">
              {usersInCrisis.length}
            </div>
            <div className="text-sm text-gray-600">Need Attention</div>
          </div>
        </div>
      </div>

      {/* Users in Crisis */}
      {usersInCrisis.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <HelpCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-green-900">No Crisis Cases</h3>
          <p className="text-green-700 mt-1">All users appear to be managing their workload well.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {usersInCrisis.map((user) => (
            <div
              key={user.id}
              className={`rounded-lg border-2 p-6 ${getSeverityColor(user.level)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5" />
                    <h3 className="font-semibold text-lg">
                      {user.display_name || user.email}
                    </h3>
                    <span className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${user.level === 'crisis' ? 'bg-red-200 text-red-800' : ''}
                      ${user.level === 'high_stress' ? 'bg-yellow-200 text-yellow-800' : ''}
                    `}>
                      {user.level.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-3">
                    <div>
                      <div className="text-2xl font-bold">{user.riskAssessment.overdueCount}</div>
                      <div className="text-sm opacity-75">Overdue Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{user.riskAssessment.criticalOverdue}</div>
                      <div className="text-sm opacity-75">Critical Overdue</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{user.riskAssessment.totalActiveTasks}</div>
                      <div className="text-sm opacity-75">Active Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {user.riskAssessment.averageReschedules.toFixed(1)}
                      </div>
                      <div className="text-sm opacity-75">Avg Reschedules</div>
                    </div>
                  </div>

                  {/* Task Details */}
                  <button
                    onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                    className="mt-4 flex items-center gap-2 text-sm font-medium hover:underline"
                  >
                    {expandedUser === user.id ? <ChevronUp /> : <ChevronDown />}
                    View Overdue Tasks ({user.overdueTasks.length})
                  </button>

                  {expandedUser === user.id && (
                    <div className="mt-3 space-y-1 text-sm">
                      {user.overdueTasks.slice(0, 5).map(task => (
                        <div key={task.id} className="flex items-center gap-2">
                          <span className={`
                            w-2 h-2 rounded-full
                            ${task.priority === 'critical' ? 'bg-red-600' : 'bg-yellow-600'}
                          `} />
                          <span>{task.title}</span>
                          <span className="text-xs opacity-75">
                            ({task.estimated_hours || 0.75}h)
                          </span>
                        </div>
                      ))}
                      {user.overdueTasks.length > 5 && (
                        <div className="opacity-75">
                          +{user.overdueTasks.length - 5} more tasks
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => generateInterventionPlan(user)}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4" />
                    Generate Plan
                  </button>

                  {selectedUser?.id === user.id && interventionPlan && (
                    <button
                      onClick={executePlan}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <PlayCircle className="w-4 h-4" />
                      Execute Plan
                    </button>
                  )}
                </div>
              </div>

              {/* Intervention Plan Preview */}
              {selectedUser?.id === user.id && interventionPlan && (
                <div className="mt-6 p-4 bg-white bg-opacity-50 rounded-lg">
                  <h4 className="font-medium mb-2">Generated Recovery Plan</h4>
                  <p className="text-sm opacity-75 mb-3">
                    {interventionPlan.explanation?.summary || 'Recovery plan generated'}
                  </p>
                  <div className="space-y-2 text-sm">
                    {interventionPlan.actions?.taskUpdates?.slice(0, 3).map((update, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="font-medium">{update.action}:</span>
                        <span>{update.reason}</span>
                      </div>
                    ))}
                    {interventionPlan.actions?.taskUpdates?.length > 3 && (
                      <div className="opacity-75">
                        +{interventionPlan.actions.taskUpdates.length - 3} more actions
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-2">Crisis Levels</h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Crisis: 10+ overdue or 3+ critical overdue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span>High Stress: 5+ overdue or 2+ critical</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Moderate: 3+ overdue tasks</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrisisMonitor;